import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { Button } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbArrowLeft } from 'react-icons/tb';
import styled from 'styled-components';
import { type Schema } from 'yup';
import { IconWrapper } from '../../../icon/IconWrapper.tsx';
import { getSearchableFieldNames } from '../../getSearchableFieldNames.ts';
import { type SearchableField } from '../../ISearchableField.tsx';
import { FilterBarEditPopoverContentBody } from './FilterBarEditPopoverContentBody.tsx';

const EditPopoverContainer = styled.div`
  min-width: 280px;
  max-width: 360px;
  display: flex;
  flex-direction: column;

  /* Compensa il padding interno di Ant Design Popover */
  margin: -12px;
`;

const EditPopoverHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 8px;
  padding-bottom: 0;
`;

export interface IFilterBarEditPopoverContentProps {
  field: SearchableField;
  fields: SearchableField[];
  /**
   * Query corrente (parsata): unica sorgente di verità per i valori iniziali
   * dell'editor. NON leggiamo dallo state RHF esterno perché può contenere
   * "fantasmi" per i tipi con rappresentazione di "vuoto" ambigua
   * (multi-select `[]`/`null`/`undefined`, radio deselezionato, …).
   */
  query: Record<string, any>;
  /** Schema dei filtri, passato al `FormHandler` annidato per cast/validazione. */
  schema: YupAnyObjectSchema;
  /**
   * Chiamata col valore confermato dal submit dell'editor. È il parent a
   * comporre qui TUTTA la logica di "applica": tradurre i valori in `setValue`
   * + `submitForm` sul form esterno E chiudere il popover (es. `popoverRef.close()`).
   */
  onApplied: (values: Record<string, unknown>) => void;
  /** Annulla: chiude e basta — il form annidato muore col mount, niente da ripristinare. */
  onCancel: () => void;
  /** Se presente, mostra l'header con freccia "←" + label (flusso pick→edit). */
  onBack?: () => void;
}

/**
 * Editor di un singolo filtro. Vive in un **`FormHandler` annidato isolato**
 * dal form esterno della `FilterBar` — separazione voluta:
 *
 * - **Init dei valori** dalla `query` (URL state), non dallo state RHF esterno.
 *   Risolve in modo strutturale la categoria di bug "il valore non si svuota
 *   davvero" su multi-select e radio.
 * - **Annulla** = smonta il form interno, niente snapshot/restore.
 * - **Applica** = submit interno → `onApplied(values)` → il parent fa
 *   `setValue` + `submitForm` sul form esterno (→ `setQuery` → URL) e chiude il popover.
 *
 * DOM-wise i due `<form>` (esterno e interno) vivono in sottoalberi disgiunti:
 * il popover renderizza il content in un portal sul `body`, quindi nessun
 * problema di HTML invalido.
 */
export function FilterBarEditPopoverContent({
  field,
  fields,
  query,
  schema,
  onApplied,
  onCancel,
  onBack,
}: Readonly<IFilterBarEditPopoverContentProps>) {
  const { t } = useTranslation();
  const fieldNames = useMemo(() => getSearchableFieldNames(field), [field]);

  // Reference stabile (deps []) per evitare il re-reset del FormHandler interno.
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot al mount intenzionale
  const initialValues = useMemo(() => {
    const r: Record<string, unknown> = {};
    fieldNames.forEach(name => {
      r[name] = query[name];
    });
    return r;
  }, []);

  // Schema di validazione del popover: ristretto ai SOLI name del campo editato
  // e con quei campi resi nullable/optional.
  //
  // pick(fieldNames): il `FormHandler` interno valida i propri valori contro lo
  // schema passato. Con lo schema INTERO della pagina, un campo `required`/`.when`
  // NON renderizzato qui (tipicamente un id di contesto obbligatorio, es.
  // `idTipoAlbo`) risulterebbe assente e farebbe fallire la validazione → "Applica"
  // bloccata → niente `onApplied`/`setQuery`. Lo restringiamo ai campi di questo
  // editor.
  //
  // nullable().optional(): svuotare un filtro è SEMPRE lecito (il chip può restare
  // come placeholder senza valore — come quelli ripristinati da localStorage).
  // Senza questo, un campo non-nullable dello schema query (es. `stato` con
  // `.oneOfEnum(...)`) farebbe fallire l'Applica a vuoto con `mixed.notNull`.
  // Rilassa SOLO lo stato vuoto: un valore non-vuoto malformato (es. data
  // invalida) continua a fallire.
  //
  // PERCHÉ È SICURO: il form ESTERNO valida comunque lo schema COMPLETO e rigido
  // sul submit reale (campi di contesto re-iniettati da `query`), quindi i vincoli
  // `required`/non-null restano garantiti a livello di ricerca.
  const editorSchema = useMemo(() => {
    const picked = schema.pick(fieldNames);
    const relaxed = Object.fromEntries(
      fieldNames.map(name => [
        name,
        // `pick` ignora silenziosamente le chiavi assenti dallo schema: per un
        // campo non mappato ripieghiamo su `mixed`, così l'editor resta
        // utilizzabile e il valore arriva comunque al form esterno (che valida
        // lo schema completo sul submit reale).
        ((picked.fields[name] as Schema | undefined) ?? yup.mixed())
          .nullable()
          .optional(),
      ]),
    );
    return picked.shape(relaxed) as YupAnyObjectSchema;
  }, [schema, fieldNames]);

  const handleInnerSubmit = useCallback(
    (innerValues: Record<string, unknown>) => {
      // Lo schema esterno copre tutta la pagina: filtriamo solo i name di questo
      // field per non sovrascrivere altre chiavi sul form esterno.
      const slice: Record<string, unknown> = {};
      fieldNames.forEach(name => {
        slice[name] = innerValues[name];
      });
      onApplied(slice);
    },
    [fieldNames, onApplied],
  );

  return (
    <EditPopoverContainer>
      {onBack && (
        <EditPopoverHeader>
          <Button
            type="text"
            size="small"
            icon={<IconWrapper icon={<TbArrowLeft />} />}
            onClick={onBack}
            aria-label={t('filter-bar.back', 'Indietro')}
          />
          <span>{field.label}</span>
        </EditPopoverHeader>
      )}
      {/* `schema={editorSchema}` (ristretto al campo), NON lo schema intero:
          evita che un `required` esterno (es. `idTipoAlbo`) blocchi l'Applica.
          Vedi il commento su `editorSchema` sopra. */}
      <FormHandler
        nested
        hideVisualFeedback
        schema={editorSchema}
        initialValues={initialValues}
        onSubmit={handleInnerSubmit}
      >
        <FilterBarEditPopoverContentBody
          field={field}
          fields={fields}
          onCancel={onCancel}
        />
      </FormHandler>
    </EditPopoverContainer>
  );
}
